"use client";

import { useEffect } from "react";

function reportError(error: Error & { digest?: string }) {
  try {
    import("@repo/analytics/client").then(({ analytics }) => {
      analytics.trackError(error);
    });
  } catch {
    // Analytics unavailable -- fail silently
  }
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    if (
      error.name === "ChunkLoadError" ||
      error.message?.includes("Loading chunk") ||
      error.message?.includes("Failed to fetch dynamically imported module")
    ) {
      const key = "venturu_chunk_reload";
      try {
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          window.location.reload();
          return;
        }
      } catch {
        // sessionStorage unavailable in some contexts
      }
    }
    reportError(error);
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100dvh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily:
            "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          background: "#FAFAFA",
          color: "#0A0A0A",
        }}
      >
        <div style={{ textAlign: "center", padding: "2rem", maxWidth: "480px" }}>
          <div
            style={{
              fontSize: "5rem",
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: "-0.03em",
              color: "rgba(10, 10, 10, 0.06)",
              marginBottom: "1rem",
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            500
          </div>

          <h1
            style={{
              fontSize: "1.5rem",
              fontWeight: 600,
              marginBottom: "0.5rem",
              lineHeight: 1.3,
            }}
          >
            Something went wrong
          </h1>

          <p
            style={{
              color: "#6B6B6B",
              fontSize: "0.938rem",
              lineHeight: 1.6,
              marginBottom: "2rem",
            }}
          >
            We hit an unexpected error. This has been reported to our team.
            {error.digest && (
              <span style={{ display: "block", marginTop: "0.5rem", fontSize: "0.75rem", color: "#B0B0B0" }}>
                Reference: {error.digest}
              </span>
            )}
          </p>

          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={reset}
              style={{
                padding: "0.625rem 1.5rem",
                fontSize: "0.875rem",
                fontWeight: 600,
                borderRadius: "0.5rem",
                border: "none",
                background: "#0A0A0A",
                color: "#FFFFFF",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Try again
            </button>
            <a
              href="/"
              style={{
                padding: "0.625rem 1.5rem",
                fontSize: "0.875rem",
                fontWeight: 600,
                borderRadius: "0.5rem",
                border: "1px solid #E8E8E8",
                background: "#FFFFFF",
                color: "#0A0A0A",
                cursor: "pointer",
                textDecoration: "none",
                fontFamily: "inherit",
              }}
            >
              Back to home
            </a>
          </div>
        </div>
      </body>
    </html>
  );
}
